/**
 * After a successful create/edit, keep the panel open if the saved roles are
 * only visible in the other app mode, and offer a mode switch to that list.
 */

import * as React from 'react';
import { useMode, type AppMode } from '@/contexts/ModeContext';
import { getCrossModeList, type RoleListModeRef } from '@/utils/cross-mode-list-utils';

/** Inputs for the cross-mode list warning after a successful save. */
interface UseCrossModeAfterSaveParams {
  roles: RoleListModeRef[];
  /** Route of the domain list after switching mode (e.g. `/admins`). */
  listPath: string;
  onSuccess?: () => void;
  onClose: () => void;
}

/** State and handlers for the cross-mode list warning after save. */
export function useCrossModeAfterSave({
  roles,
  listPath,
  onSuccess,
  onClose,
}: UseCrossModeAfterSaveParams): {
  targetMode: AppMode | null;
  finishSave: (roleIds: string[]) => void;
  handleStay: () => void;
  handleSwitch: () => void;
} {
  const { mode, setMode, canSwitchMode } = useMode();
  const [targetMode, setTargetMode] = React.useState<AppMode | null>(null);

  const finishSave = React.useCallback(
    (roleIds: string[]) => {
      const cross = canSwitchMode ? getCrossModeList(roleIds, roles, mode) : null;
      if (cross) {
        setTargetMode(cross);
        return;
      }
      onSuccess?.();
      onClose();
    },
    [canSwitchMode, mode, onClose, onSuccess, roles],
  );

  const handleStay = React.useCallback(() => {
    setTargetMode(null);
    onSuccess?.();
    onClose();
  }, [onClose, onSuccess]);

  const handleSwitch = React.useCallback(() => {
    if (targetMode) setMode(targetMode, { navigateTo: listPath });
  }, [listPath, setMode, targetMode]);

  return { targetMode, finishSave, handleStay, handleSwitch };
}
